if (!customElements.get('product-room-offer-rio')) {
  class ProductRoomOfferRio extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('.product-family-picker__details');
      this.summaryImage = this.querySelector('[data-family-summary-image]');
      this.dataElement = this.querySelector('[data-room-products]');

      if (!this.details || !this.dataElement) return;

      try {
        this.roomData = JSON.parse(this.dataElement.textContent);
      } catch (error) {
        console.error('Rio Office offer data could not be parsed.', error);
        return;
      }

      this.itemsByHandle = new Map(
        this.roomData.items.map((item) => [item.handle, item])
      );

      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleCardClick = this.handleCardClick.bind(this);
      this.handleToggle = this.handleToggle.bind(this);

      document.addEventListener('on:variant:change', this.handleVariantChange);
      document.addEventListener('click', this.handleDocumentClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.addEventListener('click', this.handleCardClick);
      this.details.addEventListener('toggle', this.handleToggle);

      this.sync(this.dataset.currentColor || '');
      this.initAnalytics();
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.handleVariantChange);
      document.removeEventListener('click', this.handleDocumentClick);
      this.removeEventListener('keydown', this.handleKeydown);
      this.removeEventListener('click', this.handleCardClick);
      this.details?.removeEventListener('toggle', this.handleToggle);
      this.analyticsObserver?.disconnect();
    }

    handleVariantChange(event) {
      const productSection = this.closest('.js-product');
      if (!productSection || !productSection.contains(event.target)) return;

      const selectedVariant = event.detail?.variant;
      const selectedColor = selectedVariant?.options?.[0] || '';
      const selectedPrice = Number(selectedVariant?.price);

      this.dataset.currentColor = selectedColor;
      if (Number.isFinite(selectedPrice)) {
        this.dataset.currentPrice = String(selectedPrice / 100);
      }

      this.sync(selectedColor);
    }

    handleDocumentClick(event) {
      if (this.details.open && !this.contains(event.target)) {
        this.details.removeAttribute('open');
      }
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' || !this.details.open) return;

      this.details.removeAttribute('open');
      this.details.querySelector('summary')?.focus();
    }

    handleCardClick(event) {
      const card = event.target.closest('[data-family-card]');
      if (!card || !this.contains(card) || card.hidden) return;
      this.publish('select', card);
    }

    handleToggle() {
      if (!this.details.open || this.openTracked) return;

      this.openTracked = true;
      this.publish('open');
    }

    initAnalytics() {
      const trackView = () => {
        if (this.viewTracked || this.hidden) return;
        this.viewTracked = true;
        this.publish('view');
      };

      if ('IntersectionObserver' in window) {
        this.analyticsObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            trackView();
            if (this.viewTracked) this.analyticsObserver.disconnect();
          }
        }, { threshold: 0.25 });
        this.analyticsObserver.observe(this);
      } else {
        trackView();
      }
    }

    getExactVariant(item, selectedColor) {
      if (!item || !selectedColor) return null;
      return item.variants.find((variant) => variant.color === selectedColor) || null;
    }

    sync(selectedColor) {
      let visibleCount = 0;
      let summaryImageUrl = '';
      let summaryImageAlt = '';

      this.querySelectorAll('[data-family-card]').forEach((card) => {
        const item = this.itemsByHandle.get(card.dataset.familyHandle);
        const targetVariant = this.getExactVariant(item, selectedColor);

        card.hidden = !targetVariant;
        card.dataset.hasColorMatch = targetVariant ? 'true' : 'false';
        if (!targetVariant) return;

        visibleCount += 1;
        card.href = `${item.url}?variant=${encodeURIComponent(targetVariant.id)}`;
        card.dataset.targetPrice = String(targetVariant.price || 0);

        const cardImage = card.querySelector('[data-family-card-image]');
        const imageUrl = targetVariant.image || item.defaultImage;
        if (!summaryImageUrl && imageUrl) {
          summaryImageUrl = imageUrl;
          summaryImageAlt = targetVariant.color || selectedColor;
        }
        if (cardImage && imageUrl) {
          cardImage.src = imageUrl;
          cardImage.alt = targetVariant.color || '';
        }

        const cardPrice = card.querySelector('[data-family-card-price]');
        if (cardPrice && targetVariant.priceFormatted) {
          cardPrice.textContent = targetVariant.priceFormatted;
        }
      });

      if (this.summaryImage && summaryImageUrl) {
        this.summaryImage.src = summaryImageUrl;
        this.summaryImage.alt = summaryImageAlt;
      }

      const countLabel = `${visibleCount} ${visibleCount === 1 ? 'piesă disponibilă' : 'piese disponibile'}`;
      this.querySelectorAll('[data-room-count]').forEach((element) => {
        element.textContent = countLabel;
      });

      const summaryCountLabel = `${visibleCount} ${visibleCount === 1 ? 'piesă' : 'piese'}${selectedColor ? ` · ${selectedColor}` : ''}`;
      this.querySelectorAll('[data-room-summary-count]').forEach((element) => {
        element.textContent = summaryCountLabel;
      });

      this.hidden = visibleCount === 0;
      if (this.hidden) this.details.removeAttribute('open');
    }

    analyticsPayload(card = null) {
      const sourcePrice = Number(this.dataset.currentPrice || 0);
      const targetPrice = Number(card?.dataset.targetPrice || 0);

      return {
        room_id: this.dataset.roomId || '',
        source_product_id: this.dataset.productId || '',
        source_handle: this.dataset.currentHandle || '',
        target_product_id: card?.dataset.targetProductId || '',
        target_handle: card?.dataset.familyHandle || '',
        source_price: sourcePrice,
        target_price: targetPrice,
        selected_color: this.dataset.currentColor || '',
        color_preserved: card ? card.dataset.hasColorMatch === 'true' : true,
        option_position: Number(card?.dataset.optionPosition || 0),
      };
    }

    publish(action, card = null) {
      const eventName = `adore_room_offer_${action}`;
      const payload = this.analyticsPayload(card);

      window.dataLayer = window.dataLayer || [];
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: eventName, room_offer: payload });
      }

      document.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
    }
  }

  customElements.define('product-room-offer-rio', ProductRoomOfferRio);
}
